// 3D scene: LIDAR terrain with swappable basemaps, the cave model, the georeferenced map sheets,
// place markers (features / observations / study points), study arcs, picking and camera moves.
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { DEPTH_RAMP, ABOVE_GROUND, SLOTS, OTHER, placeStyle, STUDY_KINDS, fmtM } from './style.js';
import { markerCanvas } from './icons.js';
import { Terrain } from './terrain.js';
import { sectionMesh, surfaceMesh } from './geology.js';
import { Labels } from './labels.js';
import { ProfileMixin } from './profileline.js';
import { WaterMixin } from './water.js';
import { CoveMixin } from './covecaves.js';

const FT = 0.3048;

// ------------------------------------------------------------------ decoding helpers
function bytes(s) { const b = atob(s); const u = new Uint8Array(b.length); for (let i = 0; i < b.length; i++) u[i] = b.charCodeAt(i); return u; }
async function gunzip(s) {
  const ds = new DecompressionStream('gzip');
  return new Response(new Blob([bytes(s)]).stream().pipeThrough(ds)).arrayBuffer();
}
const raw = (s) => bytes(s).buffer;

function loadImage(url) {
  return new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
}

// colour of value v on a scale of [value, THREE.Color] stops sorted by value
function evalStops(st, v, out) {
  if (v <= st[0][0]) return out.copy(st[0][1]);
  for (let i = 1; i < st.length; i++) if (v <= st[i][0]) {
    const t = (v - st[i - 1][0]) / ((st[i][0] - st[i - 1][0]) || 1);
    return out.copy(st[i - 1][1]).lerp(st[i][1], t);
  }
  return out.copy(st[st.length - 1][1]);
}
const OUTSIDE = new THREE.Color('#6b6a64');       // passage outside every colour band, when the bands are set to grey the rest
const OTHER_COL = new THREE.Color(OTHER);
const APPROX = new THREE.Color('#9a9a94');        // approximate (traced) caves are drawn washed out toward this grey
const DIM = new THREE.Color('#5b5a55');
const SLOT_COLS = SLOTS.map(h => new THREE.Color(h));

// glyph sprites
const iconCache = new Map();
function iconTexture(color, glyph, shape, ring) {
  const key = `${color}|${glyph}|${shape}|${ring}`;
  if (iconCache.has(key)) return iconCache.get(key);
  const t = new THREE.CanvasTexture(markerCanvas({ color, icon: glyph, shape, ring })); t.colorSpace = THREE.SRGBColorSpace;
  iconCache.set(key, t); return t;
}

export class Viewer {
  constructor(canvas, tip) {
    this.canvas = canvas; this.tip = tip;
    this.renderer = new THREE.WebGLRenderer({ antialias: true, canvas, preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.scene = new THREE.Scene(); this.scene.background = new THREE.Color('#141413');
    this.camera = new THREE.PerspectiveCamera(45, 1, 1, 40000);
    this.controls = new OrbitControls(this.camera, canvas);
    Object.assign(this.controls, { enableDamping: true, dampingFactor: 0.12, screenSpacePanning: true, maxPolarAngle: Math.PI * 0.98 });
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x4a4036, 1.1));
    const head = new THREE.DirectionalLight(0xffffff, 1.3); head.position.set(0.3, 1, 0.5); this.camera.add(head); this.scene.add(this.camera);
    this.world = new THREE.Group(); this.scene.add(this.world);
    this.cave = new THREE.Group(); this.world.add(this.cave);
    this.surfaceMarkers = new THREE.Group(); this.world.add(this.surfaceMarkers);
    this.caveMarkers = new THREE.Group(); this.cave.add(this.caveMarkers);
    this.arcs = new THREE.Group(); this.world.add(this.arcs);
    this.geoGroup = new THREE.Group(); this.world.add(this.geoGroup);
    this.sections = []; this.surfaces = new Map();
    this.opts = { opacity: 0.45, xray: true, contours: false, depressions: true, mapMode: 'cave', mapOpacity: 0.85,
      ve: 1.5, shift: 0, colorBy: 'depth', walls: true, centerline: true, trace: false, layers: { feature: true, observation: true, study_point: true },
      labels: { caves: true, features: true, observations: false, studies: false, names: true, stations: false } };
    this.filter = { caves: null, surveys: null, yearMin: null, yearMax: null, mode: 'dim' };
    this.listeners = {}; this.places = []; this.markers = []; this.dirty = true; this.pickMode = null; this.selected = null;
    this.lineMats = []; this.scheme = {}; this.clip = null;
    this.renderer.localClippingEnabled = true;
    this.controls.addEventListener('change', () => this.dirty = true);
    window.addEventListener('resize', () => this.resize());
    canvas.addEventListener('pointermove', (e) => { this.mouse = [e.clientX, e.clientY]; this.hoverDue = true; });
    canvas.addEventListener('pointerleave', () => { this.mouse = null; this.tip.style.display = 'none'; this.cursorDue = false; this.emit('cursor', null); });
    canvas.addEventListener('pointerdown', (e) => { this.down = [e.clientX, e.clientY]; });
    canvas.addEventListener('pointerup', (e) => {
      if (!this.down || Math.hypot(e.clientX - this.down[0], e.clientY - this.down[1]) > 5 || e.button !== 0) return;
      this.click(e.clientX, e.clientY);
    });
    this.labels = new Labels(this);
    this.resize();
    this.renderer.setAnimationLoop((t) => this.frame(t));
  }

  setPixelRatio(r) { this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, r)); this.resize(); }

  on(ev, fn) { (this.listeners[ev] ||= []).push(fn); }
  emit(ev, x) { (this.listeners[ev] || []).forEach(f => f(x)); }
  invalidate() { this.dirty = true; }

  resize() {
    const w = window.innerWidth, h = window.innerHeight;
    this.renderer.setSize(w, h, false); this.camera.aspect = w / h;
    const off = w > 900 ? 178 : 0;                 // keep the scene centred right of the side panel
    if (off) this.camera.setViewOffset(w, h, -off, 0, w, h); else this.camera.clearViewOffset();
    this.camera.updateProjectionMatrix();
    for (const m of this.lineMats) m.resolution.set(w, h);
    this.spriteScale = 2 / (h * this.camera.projectionMatrix.elements[5]);
    this.rescaleSprites();
    this.dirty = true;
  }

  rescaleSprites() {   // sprites inherit the vertical exaggeration from their parent; undo it
    for (const m of this.markers) m.sprite.scale.set(m.px * this.spriteScale, m.px * this.spriteScale / this.opts.ve, 1);
    for (const sp of this.labelSprites || []) sp.scale.set(sp.userData.w * this.spriteScale * 0.5, sp.userData.h * this.spriteScale * 0.5 / this.opts.ve, 1);
  }

  // ---------------------------------------------------------------- terrain
  async loadTerrain(meta) {
    this.terrain?.dispose();
    this.origin = meta.origin;
    const t = new Terrain(this, meta);
    t.threshold = this.sse ?? 12;
    await t.start();
    this.terrain = t; this.world.add(t.group);
    this.terrainMat = { uniforms: t.shared };          // the map-sheet overlay shares these uniforms
    this.applyOptions();
  }

  setQuality(q) { this.setPixelRatio(q.pixelRatio); this.sse = q.sse; if (this.terrain) this.terrain.threshold = q.sse; this.dirty = true; }

  groundLocal(x, z) { return this.terrain ? this.terrain.heightAt(x, z) : 0; }      // local units (unscaled)
  toLocal(E, N, Z) { const [xc, yc, zc] = this.origin; return new THREE.Vector3(E - xc, (Z ?? this.groundLocal(E - xc, -(N - yc)) + zc) - zc, -(N - yc)); }
  fromLocal(v) { const [xc, yc, zc] = this.origin; return { easting: v.x + xc, northing: yc - v.z, elevation: v.y + zc }; }
  groundAt(E, N) { const [xc, yc, zc] = this.origin; return this.groundLocal(E - xc, -(N - yc)) + zc; }

  setBasemap(layer) { this.terrain?.setLayer(layer); }
  setDepressionLayer(layer) { this.terrain?.setDepLayer(layer); }

  async setMapsheetTexture(url) {
    const img = await loadImage(url);
    const t = new THREE.Texture(img); t.format = THREE.RedFormat; t.needsUpdate = true;
    const u = this.terrain.shared.mapTex, old = u.value; u.value = t;
    if (old && old.image && old !== t) old.dispose();
    if (this.sheetMat) this.sheetMat.uniforms.mapTex.value = t;
    this.dirty = true;
  }

  // ---------------------------------------------------------------- cave model
  async loadModel(M) {
    this.M = M;
    this.cave.remove(...this.cave.children.filter(c => c !== this.caveMarkers));
    this.names = M.stNames ? M.stNames.split('\n') : [];
    this.nameIdx = new Map(this.names.map((n, i) => [n, i]));
    this.stPos = new Float32Array(raw(M.stPos)); this.stGround = new Float32Array(raw(M.stGround));
    this.stSurvey = new Uint16Array(raw(M.stSurvey));
    this.segs = new Uint32Array(await gunzip(M.segs)); this.segSurvey = new Uint16Array(await gunzip(M.segSurvey));
    this.tpos = new Float32Array(await gunzip(M.tube.pos)); this.tgnd = new Float32Array(await gunzip(M.tube.ground));
    this.caveIdx = new Map(M.caves.map((c, i) => [c.id, i]));
    this.surveyYear = M.surveys.map(s => (s.date ? +s.date.slice(0, 4) : null));
    const ys = this.surveyYear.filter(Boolean); this.yearRange = ys.length ? [Math.min(...ys), Math.max(...ys)] : [1950, 2025];

    // passages: rings of `ns` vertices; ring i joins ring i+1 where link[i] = 1 (older models: octagon boxes per shot)
    let ns, rsurv, link;
    if (M.tube.ns) {
      ns = M.tube.ns; rsurv = new Uint16Array(await gunzip(M.tube.survey)); link = new Uint8Array(await gunzip(M.tube.link));
    } else {
      ns = 8; const ts = new Uint16Array(await gunzip(M.tube.survey));
      rsurv = new Uint16Array(ts.length * 2); link = new Uint8Array(ts.length * 2);
      ts.forEach((v, t) => { rsurv[2 * t] = rsurv[2 * t + 1] = v; link[2 * t] = 1; });
    }
    this.tns = ns; this.rsurv = rsurv;
    const pairs = []; for (let r = 0; r < link.length; r++) if (link[r]) pairs.push(r);
    this.tpairs = Uint32Array.from(pairs);
    const per = 6 * ns, ix = new Uint32Array(pairs.length * per);
    pairs.forEach((r, k) => {
      const a = r * ns, b = a + ns, o = k * per;
      for (let j = 0; j < ns; j++) { const j2 = (j + 1) % ns, q = o + 6 * j; ix[q] = a + j; ix[q + 1] = b + j; ix[q + 2] = a + j2; ix[q + 3] = a + j2; ix[q + 4] = b + j; ix[q + 5] = b + j2; }
    });
    this.tubeIdxAll = ix;

    const tg = new THREE.BufferGeometry();
    tg.setAttribute('position', new THREE.BufferAttribute(this.tpos, 3));
    tg.setIndex(new THREE.BufferAttribute(ix.slice(), 1)); tg.computeVertexNormals();
    this.tcol = new Float32Array(this.tpos.length); tg.setAttribute('color', new THREE.BufferAttribute(this.tcol, 3));
    this.tubeMat = new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide });
    this.tubes = new THREE.Mesh(tg, this.tubeMat); this.tubes.renderOrder = 3; this.cave.add(this.tubes);

    this.clMat = new LineMaterial({ vertexColors: true, linewidth: 2.2 });
    this.dimMat = new LineMaterial({ color: 0x6b6a64, linewidth: 1.2, transparent: true, opacity: 0.55 });
    this.lineMats.push(this.clMat, this.dimMat);
    this.centerline = new LineSegments2(new LineSegmentsGeometry(), this.clMat); this.centerline.renderOrder = 3;
    this.dimLines = new LineSegments2(new LineSegmentsGeometry(), this.dimMat); this.dimLines.renderOrder = 3;
    this.cave.add(this.centerline, this.dimLines);

    const marker = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 12), new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false }));
    marker.renderOrder = 30; marker.visible = false; this.hoverMarker = marker; this.cave.add(marker);

    // map sheets hung on a surface through the station elevations
    this.mapSheet = null;
    if (M.mapsheet) {
      const S = M.mapsheet.surf, h = new Float32Array(await gunzip(S.h));
      const p = new Float32Array(S.nx * S.ny * 3), ixs = [];
      for (let r = 0; r < S.ny; r++) for (let c = 0; c < S.nx; c++) { const i = r * S.nx + c; p[3 * i] = S.x0 + c * S.dx; p[3 * i + 1] = h[i]; p[3 * i + 2] = S.z0 + r * S.dx; }
      for (let r = 0; r < S.ny - 1; r++) for (let c = 0; c < S.nx - 1; c++) { const a = r * S.nx + c; ixs.push(a, a + S.nx, a + 1, a + 1, a + S.nx, a + S.nx + 1); }
      const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.BufferAttribute(p, 3)); g.setIndex(ixs);
      const b = new THREE.Vector4(M.mapsheet.x0, M.mapsheet.x1, M.mapsheet.z0, M.mapsheet.z1);
      this.terrain.shared.mapBounds.value = b;
      this.sheetMat = new THREE.ShaderMaterial({
        uniforms: { mapTex: { value: this.terrainMat.uniforms.mapTex.value }, mapBounds: { value: b }, mapOpacity: { value: 0.85 } },
        vertexShader: `varying vec2 vXZ;
          #include <clipping_planes_pars_vertex>
          void main(){ vXZ = position.xz; vec4 mvPosition = modelViewMatrix * vec4(position,1.0); gl_Position = projectionMatrix * mvPosition;
            #include <clipping_planes_vertex>
          }`,
        fragmentShader: `uniform sampler2D mapTex; uniform vec4 mapBounds; uniform float mapOpacity; varying vec2 vXZ;
          #include <clipping_planes_pars_fragment>
          void main(){
            #include <clipping_planes_fragment>
            vec2 u = vec2((vXZ.x - mapBounds.x) / (mapBounds.y - mapBounds.x), 1.0 - (vXZ.y - mapBounds.z) / (mapBounds.w - mapBounds.z));
            float a = (u.x < 0.0 || u.x > 1.0 || u.y < 0.0 || u.y > 1.0) ? 0.0 : texture2D(mapTex, u).r * mapOpacity;
            if (a < 0.02) discard; gl_FragColor = vec4(0.96, 0.91, 0.82, a); }`,
        transparent: true, depthWrite: false, side: THREE.DoubleSide, clipping: true,
      });
      this.mapSheet = new THREE.Mesh(g, this.sheetMat); this.mapSheet.renderOrder = 2.5; this.cave.add(this.mapSheet);
    }
    // drawn (vector) maps fitted to the survey: walls and floor detail, at floor level or on the ground
    this.vmapLines = [];
    for (const [key, color, opacity] of [['walls', 0xf5e8d1, 1], ['detail', 0xcfc9ba, 0.75]]) {
      const L = M.vmap?.[key]; if (!L) continue;
      const pos = new Float32Array(await gunzip(L.pos)), gnd = new Float32Array(await gunzip(L.ground));
      const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      g.setIndex(new THREE.BufferAttribute(new Uint32Array(await gunzip(L.idx)), 1));
      const line = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ color, transparent: opacity < 1, opacity }));
      line.renderOrder = 2.6; line.userData = { pos: pos.slice(), gnd, mode: 'cave' }; this.cave.add(line); this.vmapLines.push(line);
    }
    this.labels.setMapLabels(M.vmap?.labels);
    this.rebuildCave();
    this.labels.setCaves();
    this.adj = null; this.pathCache = new Map();
    if (this.cavePlaces) this.setCavePlaces(this.cavePlaces);
    this.applyOptions();
    if (this.clip) { this.applyClipMaterials(); this.drawClipBox(); }
    if (!this.framed) { this.view('oblique', true); this.framed = true; }
  }

  surveyVisible() {
    const M = this.M, f = this.filter, vis = new Uint8Array(M.surveys.length);
    M.surveys.forEach((s, i) => {
      let ok = true;
      if (f.caves && !f.caves.has(s.cave_id)) ok = false;
      if (f.surveys && !f.surveys.has(i)) ok = false;
      const y = this.surveyYear[i];
      if (f.yearMin && (!y || y < f.yearMin)) ok = false;
      if (f.yearMax && (!y || y > f.yearMax)) ok = false;
      vis[i] = ok ? 1 : 0;
    });
    return vis;
  }

  rebuildCave() {
    if (!this.M) return;
    const vis = this.surveyVisible(); this.vis = vis;
    const S = this.stPos, segs = this.segs, on = [], off = [];
    for (let i = 0; i < this.segSurvey.length; i++) (vis[this.segSurvey[i]] ? on : off).push(i);
    const lp = (list) => { const a = new Float32Array(list.length * 6); list.forEach((k, j) => { const s0 = segs[2 * k], s1 = segs[2 * k + 1];
      a.set([S[3 * s0], S[3 * s0 + 1], S[3 * s0 + 2], S[3 * s1], S[3 * s1 + 1], S[3 * s1 + 2]], 6 * j); }); return a; };
    this.onSegs = on;
    this.centerline.geometry.dispose(); this.centerline.geometry = new LineSegmentsGeometry();
    if (on.length) this.centerline.geometry.setPositions(lp(on));
    this.dimLines.geometry.dispose(); this.dimLines.geometry = new LineSegmentsGeometry();
    if (off.length && this.filter.mode === 'dim') this.dimLines.geometry.setPositions(lp(off));
    const keep = [], per = 6 * this.tns;
    for (let k = 0; k < this.tpairs.length; k++) if (vis[this.rsurv[this.tpairs[k]]]) keep.push(k);
    const ix = new Uint32Array(keep.length * per);
    keep.forEach((k, j) => ix.set(this.tubeIdxAll.subarray(per * k, per * k + per), per * j));
    this.tubes.geometry.setIndex(new THREE.BufferAttribute(ix, 1));
    this.recolour();
  }

  // ---------------------------------------------------------------- passage colours
  // scheme (merged club default + this member's changes): { depth|elevation|year: {stops: [[value, '#hex']]}, above,
  // bands: [{mode, from, to, color, on}], outside: 'ramp'|'grey', caves: {id: '#hex'}, units: {code: '#hex'} }
  setScheme(s) { this.scheme = s || {}; this.recolour(); }

  // the colour scale of a mode as [[value, '#hex']]: depth (m below ground), elevation (m), year
  rampStops(mode) {
    const st = this.scheme?.[mode]?.stops, M = this.M;
    if (st?.length >= 2) return st;
    if (mode === 'elevation' && M) {                // highest passage lightest
      const top = M.stats.zmax + this.opts.shift, zr = (M.stats.zmax - M.stats.zmin) || 1;
      return DEPTH_RAMP.map(([v, c]) => [Math.round((top - v / 170 * zr) * 10) / 10, c]);
    }
    if (mode === 'year') { const [a, b] = this.yearRange || [1950, 2025]; return DEPTH_RAMP.map(([v, c]) => [Math.round(b - v / 170 * Math.max(1, b - a)), c]); }   // newer lighter
    return DEPTH_RAMP;
  }

  prepColours() {
    const s = this.scheme || {}, C = { bands: {} };
    for (const m of ['depth', 'elevation', 'year']) {
      C[m] = this.rampStops(m).map(([v, c]) => [+v, new THREE.Color(c)]).sort((a, b) => a[0] - b[0]);
      C.bands[m] = (s.bands || []).filter(b => (b.mode || 'depth') === m && b.on !== false && b.color && b.from !== '' && b.to !== '' && b.from != null && b.to != null)
        .map(b => ({ lo: Math.min(+b.from, +b.to), hi: Math.max(+b.from, +b.to), c: new THREE.Color(b.color) }));
    }
    C.grey = s.outside === 'grey';
    C.above = new THREE.Color(s.above || ABOVE_GROUND);
    C.caves = new Map((this.M?.caves || []).map((c, i) => [c.id, new THREE.Color(s.caves?.[c.id] || (i < 8 ? SLOTS[i] : OTHER))]));
    C.units = this.geo ? this.geo.units.map((u, k) => (s.units?.[u.code] ? new THREE.Color(s.units[u.code]) : this.geo.colors[k])) : null;
    this.C = C;
  }

  valueColour(mode, v, out) {
    const B = this.C.bands[mode];
    for (const b of B) if (v >= b.lo && v <= b.hi) return out.copy(b.c);
    if (B.length && this.C.grey) return out.copy(OUTSIDE);
    return evalStops(this.C[mode], v, out);
  }

  colourFor(y, g, si, out) {
    const o = this.opts, M = this.M;
    if (o.colorBy === 'depth') { const d = g - (y + o.shift); return d < 0 ? out.copy(this.C.above) : this.valueColour('depth', d, out); }
    if (o.colorBy === 'elevation') return this.valueColour('elevation', y + o.shift + M.origin[2], out);
    if (o.colorBy === 'cave') return out.copy(this.C.caves.get(M.surveys[si]?.cave_id) || OTHER_COL);
    if (o.colorBy === 'year') { const yr = this.surveyYear[si]; return yr ? this.valueColour('year', yr, out) : out.copy(DIM); }
    return out.set('#f07d4c');
  }

  recolour() {
    if (!this.M) return;
    this.prepColours();
    const c = new THREE.Color(), tp = this.tpos, tc = this.tcol;
    const geoCol = this.opts.colorBy === 'formation' && this.geo, U = this.C.units;
    if (geoCol) this.caveUnits();
    for (let i = 0; i < tp.length / 3; i++) {
      const si = this.rsurv[Math.floor(i / this.tns)];
      if (geoCol) c.copy(U[this.tUnit[i]]); else this.colourFor(tp[3 * i + 1], this.tgnd[i], si, c);
      if (this.M.surveys[si]?.approx) c.lerp(APPROX, 0.6);           // traced from a map, not surveyed: washed out
      tc[3 * i] = c.r; tc[3 * i + 1] = c.g; tc[3 * i + 2] = c.b;
    }
    this.tubes.geometry.attributes.color.needsUpdate = true;
    const S = this.stPos, G = this.stGround, segs = this.segs, cols = new Float32Array(this.onSegs.length * 6);
    this.onSegs.forEach((k, j) => {
      for (let e = 0; e < 2; e++) {
        const s = segs[2 * k + e];
        if (geoCol) c.copy(U[this.geo.unit(this.sVal[s])]); else this.colourFor(S[3 * s + 1], G[s], this.segSurvey[k], c);
        if (this.M.surveys[this.segSurvey[k]]?.approx) c.lerp(APPROX, 0.6);
        cols.set([c.r, c.g, c.b], 6 * j + 3 * e);
      }
    });
    if (this.onSegs.length) this.centerline.geometry.setColors(cols);
    this.dirty = true;
  }

  // ---------------------------------------------------------------- one cave's numbers (for its page)
  // classify(E, N) -> key (e.g. the drainage basin) to total the passage length by
  caveStats(id, classify) {
    const M = this.M; if (!M) return null;
    const surv = new Set(); M.surveys.forEach((s, i) => { if (s.cave_id === id) surv.add(i); });
    if (!surv.size) return null;
    const S = this.stPos, G = this.stGround, sh = this.opts.shift, [xc, yc, zc] = M.origin;
    let n = 0, lo = -1, hi = -1, deep = -1;
    for (let i = 0; i < this.names.length; i++) {
      if (!surv.has(this.stSurvey[i])) continue;
      n++;
      if (lo < 0 || S[3 * i + 1] < S[3 * lo + 1]) lo = i;
      if (hi < 0 || S[3 * i + 1] > S[3 * hi + 1]) hi = i;
      if (deep < 0 || G[i] - S[3 * i + 1] > G[deep] - S[3 * deep + 1]) deep = i;
    }
    if (!n) return { surveys: surv, stations: 0, length: 0 };
    if (this.geo) this.caveUnits();
    const units = this.geo ? new Float64Array(this.geo.units.length) : null, by = new Map(), segs = this.segs;
    let len = 0;
    for (let k = 0; k < this.segSurvey.length; k++) {
      if (!surv.has(this.segSurvey[k])) continue;
      const a = segs[2 * k], b = segs[2 * k + 1];
      const L = Math.hypot(S[3 * a] - S[3 * b], S[3 * a + 1] - S[3 * b + 1], S[3 * a + 2] - S[3 * b + 2]);
      len += L;
      if (units) units[this.geo.unit((this.sVal[a] + this.sVal[b]) / 2)] += L;
      if (classify) {
        const key = classify((S[3 * a] + S[3 * b]) / 2 + xc, yc - (S[3 * a + 2] + S[3 * b + 2]) / 2);
        if (key != null) by.set(key, (by.get(key) || 0) + L);
      }
    }
    const el = (i) => S[3 * i + 1] + sh + zc;
    return { surveys: surv, stations: n, length: len, units, by,
      high: { name: this.names[hi], elevation: el(hi) }, low: { name: this.names[lo], elevation: el(lo) },
      deepest: { name: this.names[deep], depth: G[deep] - (S[3 * deep + 1] + sh) } };
  }

  // ---------------------------------------------------------------- geology
  setGeology(geo) {
    this.geo = geo; this.tUnit = null; this.unitsFor = null;
    this.clearSections(); this.setSurfaces([]);
    if (this.M) this.recolour();
  }

  // formation of every tube vertex and the stratigraphic height of every station (after the datum shift)
  caveUnits() {
    if (!this.geo || !this.M) return;
    const sh = this.opts.shift;
    if (this.tUnit && this.unitsFor === this.M && this.unitShift === sh && this.unitGeo === this.geo) return;
    const [xc, yc, zc] = this.M.origin, G = this.geo, tp = this.tpos, S = this.stPos;
    this.tUnit = new Uint8Array(tp.length / 3);
    for (let i = 0; i < this.tUnit.length; i++) this.tUnit[i] = G.unitAt(tp[3 * i] + xc, yc - tp[3 * i + 2], tp[3 * i + 1] + zc + sh);
    this.sVal = new Float32Array(this.names.length);
    for (let i = 0; i < this.sVal.length; i++) this.sVal[i] = G.value(S[3 * i] + xc, yc - S[3 * i + 2], S[3 * i + 1] + zc + sh);
    this.unitsFor = this.M; this.unitShift = sh; this.unitGeo = G;
  }

  // passage length (m) in each formation, for the visible surveys
  formationLengths() {
    if (!this.geo || !this.M) return null;
    this.caveUnits();
    const S = this.stPos, segs = this.segs, len = new Float64Array(this.geo.units.length);
    for (const k of this.onSegs || []) {
      const a = segs[2 * k], b = segs[2 * k + 1];
      const L = Math.hypot(S[3 * a] - S[3 * b], S[3 * a + 1] - S[3 * b + 1], S[3 * a + 2] - S[3 * b + 2]);
      len[this.geo.unit((this.sVal[a] + this.sVal[b]) / 2)] += L;
    }
    return len;
  }

  async addSection(A, B, label, ends) {
    if (!this.geo) return null;
    const L = Math.hypot(B[0] - A[0], B[1] - A[1]);
    const n = Math.min(1500, Math.max(40, Math.round(L / 8)));
    const r = await fetch(`/api/terrain/profile?e0=${A[0]}&n0=${A[1]}&e1=${B[0]}&n1=${B[1]}&n=${n}`, { credentials: 'same-origin' });
    if (!r.ok) throw new Error('could not read the ground profile');
    const ground = await r.json();
    const zBase = Math.max(this.geo.zmin, Math.min(...ground) - 450);
    const mesh = sectionMesh(this.geo, A, B, ground, zBase, (E, N, Z) => this.toLocal(E, N, Z));
    const grp = new THREE.Group(); grp.add(mesh);
    (ends || ['', '']).forEach((t, i) => {
      if (!t) return;
      const P = i ? B : A, g = ground[i ? ground.length - 1 : 0];
      const sp = textSprite(t); sp.position.copy(this.toLocal(P[0], P[1], g + 40)); grp.add(sp);
      sp.scale.set(sp.userData.w * this.spriteScale * 0.5, sp.userData.h * this.spriteScale * 0.5 / this.opts.ve, 1);
      this.labelSprites = [...(this.labelSprites || []), sp];
    });
    this.geoGroup.add(grp); this.clipObject(grp);
    const s = { label, A, B, obj: grp };
    this.sections.push(s); this.dirty = true;
    return s;
  }

  removeSection(s) {
    this.geoGroup.remove(s.obj);
    s.obj.traverse(o => { o.geometry?.dispose(); o.material?.map?.dispose(); o.material?.dispose(); });
    this.sections = this.sections.filter(x => x !== s); this.dirty = true;
  }

  clearSections() { for (const s of [...this.sections]) this.removeSection(s); }

  // contact surfaces: tops of the units listed in `ks`
  setSurfaces(ks, opacity = 0.75) {
    const want = new Set(this.geo ? ks : []);
    for (const [k, m] of this.surfaces) if (!want.has(k)) { this.geoGroup.remove(m); m.geometry.dispose(); m.material.dispose(); this.surfaces.delete(k); }
    for (const k of want) {
      let m = this.surfaces.get(k);
      if (!m) {
        const G = this.geo;
        const h = G.contactSurface(G.tops[k]);
        m = surfaceMesh(G, h, G.colors[k + 1], (E, N, Z) => this.toLocal(E, N, Z), opacity, this.origin);
        this.geoGroup.add(m); this.surfaces.set(k, m); this.clipObject(m);
      }
      m.material.opacity = opacity; m.material.transparent = opacity < 1; m.material.depthWrite = opacity >= 1; m.material.needsUpdate = true;
    }
    this.dirty = true;
  }

  aboveGroundCount() {
    if (!this.M) return 0;
    let n = 0; for (let i = 0; i < this.names.length; i++) if (this.stGround[i] - (this.stPos[3 * i + 1] + this.opts.shift) < 0) n++;
    return n;
  }

  setFilter(f) { Object.assign(this.filter, f); this.rebuildCave(); this.updateArcs(); }

  setOptions(o) {
    const prevVE = this.opts.ve, prevColor = this.opts.colorBy, prevShift = this.opts.shift;
    Object.assign(this.opts, o);
    if (o.ve != null && o.ve !== prevVE) { const s = o.ve / prevVE; this.controls.target.y *= s; this.camera.position.y *= s; }
    this.applyOptions();
    if ((o.colorBy && o.colorBy !== prevColor) || (o.shift != null && o.shift !== prevShift)) this.recolour();
    if (o.shift != null && o.shift !== prevShift) { this.updateArcs(); if (this.waterDoc) { this.drawLinks(); this.highlightWater(this.waterSel); this.applyWater(); } }
    if (o.layers || o.poiHidden) { this.layoutMarkers(); this.updateArcs(); }
    if (o.labels || 'parcels' in o || 'hydro' in o) this.dirty = true;
  }

  applyOptions() {
    const o = this.opts;
    if (this.world.scale.y !== o.ve) { this.world.scale.y = o.ve; this.rescaleSprites(); if (this.clip) { this.updateClipPlanes(); this.drawClipBox(); } }
    this.cave.position.y = o.shift;
    if (this.xsObj) this.xsObj.visible = !!o.xsPoints;
    if (this.terrain) {
      const u = this.terrain.shared;
      u.contours.value = o.contours ? 1 : 0;
      if (!!u.depOn.value !== !!o.depressions) this.terrain.setDepOn(o.depressions);
      u.mapOn.value = o.mapMode === 'surface' && this.M?.mapsheet ? 1 : 0; u.mapOpacity.value = o.mapOpacity;
      this.terrain.setOpacity(o.opacity);
    }
    if (this.tubes) {
      this.tubes.visible = o.walls; this.centerline.visible = o.centerline; this.dimLines.visible = o.centerline;
      // x-ray: the cave joins the transparent pass after the terrain (which never writes depth)
      for (const m of [this.tubeMat, this.clMat, this.dimMat]) { m.transparent = o.xray || m === this.dimMat; m.needsUpdate = true; }
      const op = o.wallOpacity ?? 1;
      this.tubeMat.opacity = op; this.tubeMat.transparent = o.xray || op < 0.99; this.tubeMat.depthWrite = op >= 0.99;
    }
    for (const l of this.vmapLines || []) {
      l.visible = o.mapMode !== 'off';
      l.material.opacity = (l.userData.base ??= l.material.opacity) * o.mapOpacity; l.material.transparent = true;
      const want = o.mapMode === 'surface' ? 'surface' : 'cave';
      if (l.userData.mode !== want) {             // on the ground: swap each vertex's height for the LIDAR's
        const a = l.geometry.attributes.position, { pos, gnd } = l.userData;
        for (let i = 0; i < gnd.length; i++) a.array[3 * i + 1] = want === 'surface' ? gnd[i] + 0.4 - o.shift : pos[3 * i + 1];
        a.needsUpdate = true; l.userData.mode = want; l.userData.shift = o.shift;
      } else if (want === 'surface' && l.userData.shift !== o.shift) { l.userData.mode = null; this.applyOptions(); return; }
    }
    if (this.parcelObj) this.parcelObj.visible = !!o.parcels;
    if (this.terrain) { const T = this.terrain.shared; T.hydroOn.value = o.hydro && this.hydroDoc ? 1 : 0; T.hydroFill.value = o.hydroFill ?? 0.3; }
    if (this.springObj) this.springObj.visible = !!o.hydro;
    if (this.mapSheet) { this.mapSheet.visible = o.mapMode === 'cave'; this.sheetMat.uniforms.mapOpacity.value = o.mapOpacity; }
    this.applyWater();
    this.applyCove();
    this.dirty = true;
  }

  // ---------------------------------------------------------------- places & studies
  setPlaces(places) { this.places = places; this.layoutMarkers(); this.updateArcs(); this.labels.setCaves(); }

  setNames(list) { this.labels.setNames(list); }

  placeLocal(p) {
    const l = p.loc; if (l.easting == null) return null;
    const v = this.toLocal(l.easting, l.northing, l.on_surface ? (l.ground ?? null) : l.elevation);
    return { v, underground: !l.on_surface };
  }

  layoutMarkers() {
    for (const m of this.markers) { m.sprite.parent?.remove(m.sprite); m.sprite.material.dispose(); }
    this.markers = [];
    for (const p of this.places) {
      if (!this.opts.layers[p.type]) continue;
      if (p.type === 'study_point' && this.studyFocus && p.study_id !== this.studyFocus) continue;
      if (p.type === 'record' && this.opts.poiHidden?.includes(p.cat ?? 0)) continue;
      const pl = this.placeLocal(p); if (!pl) continue;
      const st = placeStyle(p), sel = this.selected && this.selected.type === p.type && this.selected.id === p.id;
      const shape = p.type === 'feature' ? 'circle' : p.type === 'observation' ? 'square' : p.type === 'record' ? (st.shape || 'hex') : p.kind === 'source' ? 'triangle' : 'diamond';
      const mat = new THREE.SpriteMaterial({ map: iconTexture(st.color, st.glyph, shape, sel), depthTest: false, sizeAttenuation: false });
      const sp = new THREE.Sprite(mat); sp.renderOrder = 40;
      const px = sel ? 30 : 22;
      sp.position.copy(pl.v); if (!pl.underground) sp.position.y += 0.5;
      sp.scale.set(px * this.spriteScale, px * this.spriteScale / this.opts.ve, 1);
      (pl.underground ? this.caveMarkers : this.surfaceMarkers).add(sp);
      if (this.clip && (pl.underground || this.clipTerrain)) mat.clippingPlanes = this.clipPlanes;
      this.markers.push({ sprite: sp, place: p, px });
    }
    this.dirty = true;
  }

  focusStudy(id) { this.studyFocus = id; this.layoutMarkers(); this.updateArcs(); }

  updateArcs() {
    for (const c of [...this.arcs.children]) { this.arcs.remove(c); c.geometry.dispose(); }
    if (!this.opts.layers.study_point) return;
    const byStudy = new Map();
    for (const p of this.places) if (p.type === 'study_point') {
      if (this.studyFocus && p.study_id !== this.studyFocus) continue;
      (byStudy.get(p.study_id) || byStudy.set(p.study_id, []).get(p.study_id)).push(p);
    }
    const pos = [], neg = [];
    const world = (p) => { const pl = this.placeLocal(p); if (!pl) return null; const v = pl.v.clone(); if (pl.underground) v.y += this.opts.shift; return v; };
    for (const pts of byStudy.values()) {
      const src = pts.filter(p => p.kind === 'source'), rec = pts.filter(p => p.kind === 'receptor');
      for (const s of src) for (const r of rec) {
        const a = world(s), b = world(r); if (!a || !b) continue;
        const d = a.distanceTo(b), mid = a.clone().add(b).multiplyScalar(0.5); mid.y = Math.max(a.y, b.y) + Math.max(20, d * 0.25);
        const curve = new THREE.QuadraticBezierCurve3(a, mid, b), list = r.result === 'positive' ? pos : neg;
        const ps = curve.getPoints(40);
        for (let i = 0; i < ps.length - 1; i++) list.push(ps[i].x, ps[i].y, ps[i].z, ps[i + 1].x, ps[i + 1].y, ps[i + 1].z);
      }
    }
    const mk = (arr, color, dashed) => {
      if (!arr.length) return;
      const g = new LineSegmentsGeometry(); g.setPositions(arr);
      const m = new LineMaterial({ color, linewidth: dashed ? 1.6 : 2.6, transparent: true, opacity: 0.95, depthTest: false, dashed, dashSize: 6, gapSize: 5 });
      m.resolution.set(window.innerWidth, window.innerHeight); this.lineMats.push(m);
      const l = new LineSegments2(g, m); if (dashed) l.computeLineDistances(); l.renderOrder = 35; this.arcs.add(l);
    };
    mk(pos, 0xd55181, false); mk(neg, 0x9c9b93, true);
    this.dirty = true;
  }

  select(sel) { this.selected = sel; this.layoutMarkers(); }

  // ---------------------------------------------------------------- named places in caves
  // The survey stations between two stations (shortest path through the shots), as station indices.
  stationPath(a, b) {
    const ia = this.nameIdx?.get(a), ib = this.nameIdx?.get(b);
    if (ia == null) return [];
    if (ib == null || ia === ib) return [ia];
    const key = ia + ':' + ib;
    if (this.pathCache.has(key)) return this.pathCache.get(key);
    if (!this.adj) {
      this.adj = Array.from({ length: this.names.length }, () => []);
      for (let k = 0; k < this.segs.length / 2; k++) { const u = this.segs[2 * k], v = this.segs[2 * k + 1]; this.adj[u].push(v); this.adj[v].push(u); }
    }
    const prev = new Int32Array(this.names.length).fill(-1), q = [ia]; prev[ia] = ia;
    for (let h = 0; h < q.length && prev[ib] < 0; h++) for (const v of this.adj[q[h]]) if (prev[v] < 0) { prev[v] = q[h]; q.push(v); }
    let path = [];
    if (prev[ib] >= 0) { for (let v = ib; v !== ia; v = prev[v]) path.push(v); path.push(ia); path.reverse(); }
    else path = [ia, ib];                                 // not connected: just the two ends
    this.pathCache.set(key, path);
    return path;
  }

  // length along the survey and elevation range of a place given by its stations
  placeStats(stations) {
    if (!this.M || !stations?.length) return null;
    const P = this.stationPath(stations[0], stations[1] ?? stations[0]); if (!P.length) return null;
    const S = this.stPos, sh = this.opts.shift, zc = this.M.origin[2];
    let len = 0, lo = Infinity, hi = -Infinity, deep = -Infinity;
    P.forEach((i, k) => {
      const z = S[3 * i + 1]; lo = Math.min(lo, z); hi = Math.max(hi, z); deep = Math.max(deep, this.stGround[i] - (z + sh));
      if (k) { const j = P[k - 1]; len += Math.hypot(S[3 * i] - S[3 * j], S[3 * i + 1] - S[3 * j + 1], S[3 * i + 2] - S[3 * j + 2]); }
    });
    return { stations: P.length, length: len, low: lo + sh + zc, high: hi + sh + zc, deepest: deep };
  }

  // a point in cave-local coordinates for a place: its station, or half way along its stretch
  placePoint(stations) {
    const P = this.stationPath(stations[0], stations[1] ?? stations[0]); if (!P.length) return null;
    const S = this.stPos;
    if (P.length === 1) return [S[3 * P[0]], S[3 * P[0] + 1], S[3 * P[0] + 2]];
    const d = [0]; for (let k = 1; k < P.length; k++) { const i = P[k], j = P[k - 1]; d.push(d[k - 1] + Math.hypot(S[3 * i] - S[3 * j], S[3 * i + 2] - S[3 * j + 2])); }
    const half = d[d.length - 1] / 2; let k = d.findIndex(x => x >= half); if (k < 1) k = 1;
    const i = P[k], j = P[k - 1], t = (half - d[k - 1]) / ((d[k] - d[k - 1]) || 1);
    return [S[3 * j] + (S[3 * i] - S[3 * j]) * t, S[3 * j + 1] + (S[3 * i + 1] - S[3 * j + 1]) * t, S[3 * j + 2] + (S[3 * i + 2] - S[3 * j + 2]) * t];
  }

  // list: [{id, cave_id, parent_id, name, kind, stations, value, unit, note}] -> labels
  setCavePlaces(list) {
    this.cavePlaces = list || [];
    if (!this.M) return;
    const out = [];
    for (const p of this.cavePlaces) {
      if (!p.stations?.length) continue;
      const q = this.placePoint(p.stations); if (!q) continue;
      out.push({ id: p.id, text: p.note ? p.label : p.name, kind: p.note ? 'cnote' : 'cplace', top: !p.parent_id, x: q[0], y: q[1], z: q[2], norm: p.norm });
    }
    this.labels.setPlaces(out);
  }

  // draw a place (its stretch of survey, or a ring at its station) in bright yellow; null clears it
  highlightPlace(stations) {
    if (this.hlObj) { this.cave.remove(this.hlObj); this.hlObj.traverse(o => o.geometry?.dispose()); this.hlObj = null; }
    if (!stations?.length || !this.M) { this.dirty = true; return; }
    const P = this.stationPath(stations[0], stations[1] ?? stations[0]), S = this.stPos, grp = new THREE.Group();
    if (P.length > 1) {
      const arr = []; for (let k = 1; k < P.length; k++) { const i = P[k - 1], j = P[k]; arr.push(S[3 * i], S[3 * i + 1], S[3 * i + 2], S[3 * j], S[3 * j + 1], S[3 * j + 2]); }
      const g = new LineSegmentsGeometry(); g.setPositions(arr);
      if (!this.hlMat) { this.hlMat = new LineMaterial({ color: 0xffd23f, linewidth: 6, depthTest: false, transparent: true, opacity: 0.9 }); this.hlMat.resolution.set(innerWidth, innerHeight); this.lineMats.push(this.hlMat); }
      const l = new LineSegments2(g, this.hlMat); l.renderOrder = 43; grp.add(l);
    }
    const pts = new THREE.BufferGeometry().setFromPoints(P.length > 1 ? [P[0], P[P.length - 1]].map(i => new THREE.Vector3(S[3 * i], S[3 * i + 1], S[3 * i + 2]))
      : [new THREE.Vector3(S[3 * P[0]], S[3 * P[0] + 1], S[3 * P[0] + 2])]);
    const dots = new THREE.Points(pts, new THREE.PointsMaterial({ color: 0xffd23f, size: P.length > 1 ? 10 : 16, sizeAttenuation: false, depthTest: false }));
    dots.renderOrder = 44; grp.add(dots);
    this.hlObj = grp; this.cave.add(grp); this.dirty = true;
  }

  // ---------------------------------------------------------------- the survey open in the data panel
  // focus = { stations: [index], shots: [[i, j]], active: [i, j] | null }: its shots drawn bright, the active one
  // brighter still, and its stations labelled whatever the zoom (labels.js reads this.surveyFocus)
  setSurveyFocus(focus) {
    if (this.sfObj) { this.cave.remove(this.sfObj); this.sfObj.traverse(o => o.geometry?.dispose()); this.sfObj = null; }
    this.surveyFocus = focus && focus.stations?.length ? { ...focus, set: new Set(focus.stations) } : null;
    if (!this.surveyFocus || !this.M) { this.dirty = true; this.labels?.update?.(); return; }
    const S = this.stPos, grp = new THREE.Group(), seg = (pairs) => {
      const arr = []; for (const [i, j] of pairs) arr.push(S[3 * i], S[3 * i + 1], S[3 * i + 2], S[3 * j], S[3 * j + 1], S[3 * j + 2]);
      const g = new LineSegmentsGeometry(); g.setPositions(arr); return g;
    };
    if (!this.sfMat) {
      this.sfMat = new LineMaterial({ color: 0x5ad1f0, linewidth: 3.5, depthTest: false, transparent: true, opacity: 0.85 });
      this.sfActMat = new LineMaterial({ color: 0xffd23f, linewidth: 7, depthTest: false, transparent: true, opacity: 0.95 });
      for (const m of [this.sfMat, this.sfActMat]) { m.resolution.set(innerWidth, innerHeight); this.lineMats.push(m); }
    }
    if (focus.shots?.length) { const l = new LineSegments2(seg(focus.shots), this.sfMat); l.renderOrder = 42; grp.add(l); }
    if (focus.active) { const l = new LineSegments2(seg([focus.active]), this.sfActMat); l.renderOrder = 43; grp.add(l); }
    const pts = new THREE.BufferGeometry().setFromPoints(focus.stations.map(i => new THREE.Vector3(S[3 * i], S[3 * i + 1], S[3 * i + 2])));
    const dots = new THREE.Points(pts, new THREE.PointsMaterial({ color: 0x5ad1f0, size: 6, sizeAttenuation: false, depthTest: false }));
    dots.renderOrder = 44; grp.add(dots);
    this.sfObj = grp; this.cave.add(grp); this.dirty = true;
  }

  flyToShot(i, j) {
    const S = this.stPos, c = new THREE.Vector3((S[3 * i] + S[3 * j]) / 2, (S[3 * i + 1] + S[3 * j + 1]) / 2, (S[3 * i + 2] + S[3 * j + 2]) / 2);
    const L = Math.hypot(S[3 * i] - S[3 * j], S[3 * i + 1] - S[3 * j + 1], S[3 * i + 2] - S[3 * j + 2]);
    this.flyToLocal(c, Math.max(35, L * 2.5), true);
  }

  // the survey shot nearest a screen point: {a, b (station names), along (m from a), t (0-1)}
  nearestShot(x, y, rad = 14) {
    if (!this.M || !this.segs?.length) return null;
    this.cave.updateMatrixWorld();
    const S = this.stPos, m = this.cave.matrixWorld, r = this.canvas.getBoundingClientRect(), v = new THREE.Vector3();
    const scr = (i) => { v.set(S[3 * i], S[3 * i + 1], S[3 * i + 2]).applyMatrix4(m).project(this.camera);
      return v.z > 1 ? null : [(v.x + 1) / 2 * r.width + r.left, (1 - v.y) / 2 * r.height + r.top]; };
    let best = null, bd = rad;
    for (let k = 0; k < this.segs.length / 2; k++) {
      const i = this.segs[2 * k], j = this.segs[2 * k + 1];
      if (this.vis && !this.vis[this.stSurvey[i]] && this.filter.mode === 'hide') continue;
      const a = scr(i), b = scr(j); if (!a || !b) continue;
      const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy; if (L2 < 4) continue;
      const t = Math.max(0, Math.min(1, ((x - a[0]) * dx + (y - a[1]) * dy) / L2));
      const d = Math.hypot(a[0] + dx * t - x, a[1] + dy * t - y);
      if (d < bd) { bd = d; best = { i, j, t }; }
    }
    if (!best) return null;
    const { i, j, t } = best, L = Math.hypot(S[3 * i] - S[3 * j], S[3 * i + 1] - S[3 * j + 1], S[3 * i + 2] - S[3 * j + 2]);
    return { a: this.names[i], b: this.names[j], ia: i, ib: j, t, along: t * L, length: L };
  }

  // drawn cross-sections, as small squares on the survey line (opts.xsPoints); list = [{id, e, n, z, ...}]
  setXsPoints(list) {
    if (this.xsObj) { this.cave.remove(this.xsObj); this.xsObj.geometry.dispose(); this.xsObj = null; }
    this.xsList = (list || []).filter(x => x.e != null && !x.disabled);
    if (!this.xsList.length || !this.M) { this.dirty = true; return; }
    const [xc, yc, zc] = this.M.origin;
    const g = new THREE.BufferGeometry().setFromPoints(this.xsList.map(x => new THREE.Vector3(x.e - xc, x.z - zc, -(x.n - yc))));
    if (!this.xsTex) {
      const cv = document.createElement('canvas'); cv.width = cv.height = 32; const q = cv.getContext('2d');
      q.translate(16, 16); q.rotate(Math.PI / 4); q.fillStyle = '#141413'; q.fillRect(-11, -11, 22, 22);
      q.fillStyle = '#ffffff'; q.fillRect(-9, -9, 18, 18); q.fillStyle = '#d95926'; q.fillRect(-6, -6, 12, 12);
      this.xsTex = new THREE.CanvasTexture(cv);
    }
    this.xsObj = new THREE.Points(g, new THREE.PointsMaterial({ size: 20, sizeAttenuation: false, map: this.xsTex, transparent: true, alphaTest: 0.3, depthTest: false }));
    this.xsObj.renderOrder = 41; this.xsObj.visible = !!this.opts.xsPoints; this.cave.add(this.xsObj); this.dirty = true;
  }

  nearestXsPoint(x, y, rad = 10) {
    if (!this.xsObj?.visible) return null;
    this.cave.updateMatrixWorld();
    const a = this.xsObj.geometry.attributes.position, v = new THREE.Vector3();
    let best = null, bd = rad * rad;
    for (let k = 0; k < this.xsList.length; k++) {
      v.fromBufferAttribute(a, k); const s = this.screen(v, this.cave); if (!s) continue;
      const d = (s[0] - x) ** 2 + (s[1] - y) ** 2; if (d < bd) { bd = d; best = this.xsList[k]; }
    }
    return best;
  }

  flyToPlaceStations(stations) {
    if (!stations?.length || !this.M) return;
    const P = this.stationPath(stations[0], stations[1] ?? stations[0]), S = this.stPos, b = new THREE.Box3(), v = new THREE.Vector3();
    for (const i of P) b.expandByPoint(v.set(S[3 * i], S[3 * i + 1], S[3 * i + 2]));
    if (b.isEmpty()) return;
    const c = b.getCenter(new THREE.Vector3()), s = b.getSize(new THREE.Vector3());
    this.flyToLocal(c, Math.max(40, Math.hypot(s.x, s.z) * 0.7), true);
  }

  // ---------------------------------------------------------------- section slicer
  // Keeps only what lies inside a box: clip = { x: [e0, e1], y: [n0, n1], z: [z0, z1] (each optional; easting, northing,
  // elevation in metres), terrain: also cut the ground, box: draw the box }.  Narrow one axis to a slab to see a section.
  setClip(clip) {
    const on = !!(clip && (clip.x || clip.y || clip.z));
    const was = !!this.clip, wasT = !!this.clipTerrain;
    this.clip = on ? clip : null; this.clipTerrain = on && !!clip.terrain;
    this.clipPlanes ||= Array.from({ length: 6 }, () => new THREE.Plane());
    this.updateClipPlanes();
    if (on !== was || this.clipTerrain !== wasT) this.applyClipMaterials();
    this.drawClipBox();
    this.dirty = true;
  }

  updateClipPlanes() {
    const c = this.clip, P = this.clipPlanes;
    if (!c || !this.origin) return;
    const [xc, yc, zc] = this.origin, ve = this.opts.ve, BIG = 1e7, n = (x, y, z) => new THREE.Vector3(x, y, z);
    P[0].set(n(1, 0, 0), c.x ? -(c.x[0] - xc) : BIG); P[1].set(n(-1, 0, 0), c.x ? c.x[1] - xc : BIG);
    P[2].set(n(0, 0, 1), c.y ? -(yc - c.y[1]) : BIG); P[3].set(n(0, 0, -1), c.y ? yc - c.y[0] : BIG);       // local z = northing flipped
    P[4].set(n(0, 1, 0), c.z ? -(c.z[0] - zc) * ve : BIG); P[5].set(n(0, -1, 0), c.z ? (c.z[1] - zc) * ve : BIG);
  }

  // underground things are always cut; things on the surface only when the ground is cut too
  clipObject(obj, surface = false) {
    const planes = this.clip && (!surface || this.clipTerrain) ? this.clipPlanes : null;
    if (!obj) return;
    obj.traverse(o => { const ms = o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : [];
      for (const m of ms) { if ((m.clippingPlanes || null) !== planes) { m.clippingPlanes = planes; m.needsUpdate = true; } } });
  }

  applyClipMaterials() {
    for (const o of this.cave.children) if (o !== this.hoverMarker) this.clipObject(o);
    this.clipObject(this.geoGroup);
    for (const o of [this.surfaceMarkers, this.springObj, this.sinkObj, this.parcelObj, this.linkObj, this.sinkPtObj]) this.clipObject(o, true);
    this.terrain?.setClip(this.clipTerrain ? this.clipPlanes : null);
  }

  inClipLocal(x, y, z) {                                 // local x, elevation-above-origin (with the datum shift), z
    const c = this.clip; if (!c) return true;
    const [xc, yc, zc] = this.origin, E = x + xc, N = yc - z, Z = y + zc;
    return (!c.x || (E >= c.x[0] && E <= c.x[1])) && (!c.y || (N >= c.y[0] && N <= c.y[1])) && (y == null || !c.z || (Z >= c.z[0] && Z <= c.z[1]));
  }

  inClip(i) {                                           // is station i inside the slicer box?
    const c = this.clip; if (!c) return true;
    const [xc, yc, zc] = this.M.origin, S = this.stPos, E = S[3 * i] + xc, N = yc - S[3 * i + 2], Z = S[3 * i + 1] + this.opts.shift + zc;
    return (!c.x || (E >= c.x[0] && E <= c.x[1])) && (!c.y || (N >= c.y[0] && N <= c.y[1])) && (!c.z || (Z >= c.z[0] && Z <= c.z[1]));
  }

  // the extent the slicer works in: the surveyed caves (or the whole area), with a margin
  clipExtent() {
    const [xc, yc, zc] = this.origin, b = this.M && this.names.length ? this.bounds() : this.areaBounds(), pad = 40;
    let gmax = b.max.y; if (this.stGround) for (let i = 0; i < this.stGround.length; i++) gmax = Math.max(gmax, this.stGround[i]);
    return { x: [Math.floor(b.min.x + xc - pad), Math.ceil(b.max.x + xc + pad)], y: [Math.floor(yc - b.max.z - pad), Math.ceil(yc - b.min.z + pad)],
      z: [Math.floor(b.min.y + zc + this.opts.shift - 15), Math.ceil(gmax + zc + 15)] };
  }

  drawClipBox() {
    this.drawClipProfile();
    if (this.clipBox) { this.world.remove(this.clipBox); this.clipBox.geometry.dispose(); this.clipBox = null; }
    const c = this.clip; if (!c || c.box === false || !this.origin) return;
    const X = this.clipExtent(), [xc, yc, zc] = this.origin;
    const x = c.x || X.x, y = c.y || X.y, z = c.z || X.z;
    const box = new THREE.Box3(new THREE.Vector3(x[0] - xc, z[0] - zc, yc - y[1]), new THREE.Vector3(x[1] - xc, z[1] - zc, yc - y[0]));
    const g = new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1));
    const m = new THREE.LineBasicMaterial({ color: 0xffd23f, transparent: true, opacity: 0.45, depthTest: false });
    this.clipBox = new THREE.LineSegments(g, m); this.clipBox.renderOrder = 7;
    box.getCenter(this.clipBox.position); box.getSize(this.clipBox.scale);
    this.world.add(this.clipBox);
  }

  // the ground line along a thin east-west or north-south slab, so a cross-section shows how deep things are
  drawClipProfile() {
    if (this.clipProfile) { this.world.remove(this.clipProfile); this.clipProfile.geometry.dispose(); this.clipProfile = null; }
    const c = this.clip; if (!c || !this.origin || !this.terrain) return;
    const X = this.clipExtent(), [xc, yc, zc] = this.origin, seg = [];
    const thin = (k) => c[k] && c[k][1] - c[k][0] <= 300;
    const run = (k) => {                       // k: the thin axis; draw along the other one at the slab's middle
      const mid = (c[k][0] + c[k][1]) / 2, o = k === 'x' ? 'y' : 'x', r = c[o] || X[o], n = Math.min(4000, Math.ceil((r[1] - r[0]) / 4));
      let prev = null;
      for (let i = 0; i <= n; i++) {
        const t = r[0] + (r[1] - r[0]) * i / n, E = k === 'x' ? mid : t, N = k === 'x' ? t : mid, Z = this.groundAt(E, N);
        const v = [E - xc, Z - zc, -(N - yc)];
        if (prev) seg.push(...prev, ...v); prev = v;
      }
    };
    if (thin('x')) run('x'); if (thin('y')) run('y');
    if (!seg.length) return;
    const g = new LineSegmentsGeometry(); g.setPositions(new Float32Array(seg));
    this.profileMat ||= (() => { const m = new LineMaterial({ color: 0x9fe07a, linewidth: 2.5 }); m.resolution.set(innerWidth, innerHeight); this.lineMats.push(m); return m; })();
    this.clipProfile = new LineSegments2(g, this.profileMat); this.clipProfile.renderOrder = 9;
    this.world.add(this.clipProfile);
  }

  // look straight across a slab: along the axis that was cut thinnest
  // focus: {easting, northing, elevation} to centre on (e.g. the selected station), else the middle of the box
  lookAcross(axis, focus) {
    const X = this.clipExtent(), c = this.clip || {}, [xc, yc, zc] = this.origin;
    const r = (k) => c[k] || X[k], mid = (k) => (r(k)[0] + r(k)[1]) / 2;
    const t = focus ? new THREE.Vector3(focus.easting - xc, (focus.elevation - zc) * this.opts.ve, yc - focus.northing)
      : new THREE.Vector3(mid('x') - xc, (mid('z') - zc) * this.opts.ve, yc - mid('y'));
    const span = focus ? (focus.span || 220) : Math.max(r('x')[1] - r('x')[0], r('y')[1] - r('y')[0]) * 0.75 + 100;
    const dir = { x: [1, 0.001, 0], y: [0, 0.001, 1], z: [0, 1, 0.0005] }[axis];
    this.flyTo(t, t.clone().add(new THREE.Vector3(...dir).normalize().multiplyScalar(span)));
  }

  // ---------------------------------------------------------------- picking
  screen(v, parent) {
    const p = v.clone(); if (parent) p.applyMatrix4(parent.matrixWorld);
    p.project(this.camera);
    if (p.z > 1) return null;
    const r = this.canvas.getBoundingClientRect();
    return [(p.x + 1) / 2 * r.width + r.left, (1 - p.y) / 2 * r.height + r.top, p.z];
  }

  nearestMarker(x, y, rad = 16) {
    let best = null, bd = rad * rad;
    this.world.updateMatrixWorld();
    for (const m of this.markers) {
      if (this.clip) { const q = m.sprite.position, under = m.sprite.parent === this.caveMarkers;
        if ((under || this.clipTerrain) && !this.inClipLocal(q.x, q.y + (under ? this.opts.shift : 0), q.z)) continue; }
      const s = this.screen(m.sprite.position, m.sprite.parent); if (!s) continue;
      const d = (s[0] - x) ** 2 + (s[1] - y) ** 2; if (d < bd) { bd = d; best = m; }
    }
    return best;
  }

  nearestStation(x, y, rad = 14) {
    if (!this.M || !(this.opts.walls || this.opts.centerline)) return -1;
    this.cave.updateMatrixWorld();
    const v = new THREE.Vector3(), m = this.cave.matrixWorld, r = this.canvas.getBoundingClientRect();
    let best = -1, bd = rad * rad;
    for (let i = 0; i < this.names.length; i++) {
      if (this.vis && !this.vis[this.stSurvey[i]] && this.filter.mode === 'hide') continue;
      if (this.clip && !this.inClip(i)) continue;
      v.set(this.stPos[3 * i], this.stPos[3 * i + 1], this.stPos[3 * i + 2]).applyMatrix4(m).project(this.camera);
      if (v.z > 1) continue;
      const d = ((v.x + 1) / 2 * r.width + r.left - x) ** 2 + ((1 - v.y) / 2 * r.height + r.top - y) ** 2;
      if (d < bd) { bd = d; best = i; }
    }
    return best;
  }

  groundRay(x, y) {
    if (!this.terrain) return null;
    const r = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2((x - r.left) / r.width * 2 - 1, -((y - r.top) / r.height) * 2 + 1);
    const ray = new THREE.Raycaster(); ray.setFromCamera(ndc, this.camera);
    const o = ray.ray.origin, d = ray.ray.direction, ve = this.opts.ve;
    const above = (t) => { const p = o.clone().addScaledVector(d, t); return p.y - this.groundLocal(p.x, p.z) * ve; };
    let t0 = 0, step = 2, prev = above(0);
    for (let t = step; t < 60000; t += step) {
      const h = above(t);
      if (h < 0 && prev >= 0) { let a = t - step, b = t; for (let k = 0; k < 30; k++) { const m = (a + b) / 2; if (above(m) > 0) a = m; else b = m; }
        const p = o.clone().addScaledVector(d, b); p.y = this.groundLocal(p.x, p.z); return p; }
      prev = h; step = Math.min(Math.max(4, t * 0.004), step * 1.05); t0 = t;
    }
    return null;
  }

  stationInfo(i) {
    const M = this.M, sv = M.surveys[this.stSurvey[i]] || {};
    const elev = this.stPos[3 * i + 1] + this.opts.shift + M.origin[2], gnd = this.stGround[i] + M.origin[2];
    const [xc, yc] = M.origin;
    let formation = null;
    if (this.geo) { this.caveUnits(); formation = this.geo.describe(this.sVal[i]); }
    return { type: 'station', id: this.names[i], name: this.names[i], survey: sv, elevation: elev, ground: gnd, depth: gnd - elev,
      easting: this.stPos[3 * i] + xc, northing: yc - this.stPos[3 * i + 2], formation };
  }

  click(x, y) {
    if (this.pickMode) {
      const mode = this.pickMode.kind;
      if (mode === 'shot') {
        const h = this.nearestShot(x, y, 16); if (!h) return;
        this.finishPick(h); return;
      }
      if (mode === 'station') {
        const i = this.nearestStation(x, y, 20); if (i < 0) return;
        const s = this.stationInfo(i); this.finishPick({ mode: 'station', station: s.name, easting: s.easting, northing: s.northing, elevation: s.elevation });
      } else {
        const p = this.groundRay(x, y); if (!p) return;
        const g = this.fromLocal(p); this.finishPick({ mode: 'surface', easting: g.easting, northing: g.northing, elevation: g.elevation });
      }
      return;
    }
    if (this.lineDraw) { this.addProfilePoint(x, y); return; }
    if (this.labelPick) {                                  // making a profile label from something on the map
      const m = this.nearestMarker(x, y);
      if (m) { const l = m.place.loc || {}; this.labelPick({ kind: m.place.kind === 'entrance' ? 'entrance' : m.place.type, text: m.place.name, E: l.easting, N: l.northing,
        Z: l.on_surface === false && l.elevation != null ? l.elevation : (l.ground ?? l.elevation ?? this.groundAt(l.easting, l.northing)) }); return; }
      const i = this.nearestStation(x, y);
      if (i >= 0) { const s = this.stationInfo(i); this.labelPick({ kind: 'station', text: s.name, E: s.easting, N: s.northing, Z: s.elevation }); }
      return;
    }
    if (this.measuring) { this.addMeasurePoint(x, y); return; }
    const m = this.nearestMarker(x, y);
    if (m) { this.emit('select', { type: m.place.type, id: m.place.id, place: m.place }); return; }
    const sk = this.nearestSink(x, y);
    if (sk) { this.emit('select', { type: 'sink', id: sk.id, sink: sk }); return; }
    const sp = this.nearestSpring(x, y);
    if (sp) { this.emit('select', { type: 'spring', id: sp.key, spring: sp }); return; }
    const w = this.nearestWater(x, y, 8, false);           // sinking points and connections before stations ...
    if (w) { this.emit('select', w); return; }
    const xp = this.nearestXsPoint(x, y);
    if (xp) { this.emit('select', { type: 'xspoint', id: xp.id, xs: xp }); return; }
    const i = this.nearestStation(x, y);
    if (i >= 0) { this.emit('select', this.stationInfo(i)); return; }
    const wr = this.nearestWater(x, y, 8, true);           // ... streams in caves after them (they run through the stations)
    if (wr) { this.emit('select', wr); return; }
    const dep = this.nearestDepression(x, y);
    if (dep) { this.emit('select', { type: 'depression', id: dep.i, ...dep.d }); return; }
    const g = this.groundRay(x, y);              // bare ground: where is this?
    if (g) { const p = this.fromLocal(g); this.emit('select', { type: 'point', id: `${p.easting.toFixed(1)},${p.northing.toFixed(1)}`, ...p }); }
  }

  // ---------------------------------------------------------------- what lies around a ground point
  groundInfo(E, N) {
    const z = this.groundAt(E, N), d = 2;
    const dzdx = (this.groundAt(E + d, N) - this.groundAt(E - d, N)) / (2 * d), dzdy = (this.groundAt(E, N + d) - this.groundAt(E, N - d)) / (2 * d);
    const slope = Math.atan(Math.hypot(dzdx, dzdy)) * 180 / Math.PI;
    const aspect = ((Math.atan2(-dzdx, -dzdy) * 180 / Math.PI) + 360) % 360;      // the way the slope faces (downhill)
    let formation = null;
    if (this.geo) formation = this.geo.describe(this.geo.value(E, N, z - 0.5));
    return { elevation: z, slope, aspect, formation, passage: this.nearestPassage(E, N) };
  }

  nearestPassage(E, N) {
    if (!this.M || !this.names.length) return null;
    const [xc, yc] = this.M.origin, x = E - xc, zz = -(N - yc), S = this.stPos;
    let best = -1, bd = Infinity;
    for (let i = 0; i < this.names.length; i++) {
      if (this.vis && !this.vis[this.stSurvey[i]]) continue;
      const d = (S[3 * i] - x) ** 2 + (S[3 * i + 2] - zz) ** 2;
      if (d < bd) { bd = d; best = i; }
    }
    return best < 0 ? null : { ...this.stationInfo(best), dist: Math.sqrt(bd) };
  }

  // ---------------------------------------------------------------- measuring
  startMeasure() { this.measuring = true; this.measurePts ||= []; this.canvas.style.cursor = 'crosshair'; this.drawMeasure(); }
  stopMeasure() { this.measuring = false; this.canvas.style.cursor = ''; this.drawMeasure(); }
  clearMeasure() { this.measurePts = []; this.drawMeasure(); }
  undoMeasure() { this.measurePts?.pop(); this.drawMeasure(); }
  measureFrom(E, N, Z, label) { this.measurePts = [{ easting: E, northing: N, elevation: Z ?? this.groundAt(E, N), label: label || null, kind: 'ground' }]; this.startMeasure(); }

  addMeasurePoint(x, y) {
    const i = this.nearestStation(x, y, 12);
    let p;
    if (i >= 0) { const s = this.stationInfo(i); p = { easting: s.easting, northing: s.northing, elevation: s.elevation, label: s.name, kind: 'station' }; }
    else { const g = this.groundRay(x, y); if (!g) return; p = { ...this.fromLocal(g), kind: 'ground' }; }
    (this.measurePts ||= []).push(p); this.drawMeasure();
  }

  drawMeasure() {
    if (this.measureObj) { this.world.remove(this.measureObj); this.measureObj.traverse(o => { o.geometry?.dispose(); }); this.measureObj = null; }
    const P = this.measurePts || [];
    const labels = [];
    if (P.length && this.origin) {
      const [xc, yc, zc] = this.origin, grp = new THREE.Group();
      const loc = P.map(p => new THREE.Vector3(p.easting - xc, p.elevation - zc, -(p.northing - yc)));
      const dots = new THREE.BufferGeometry().setFromPoints(loc);
      const pm = new THREE.PointsMaterial({ color: 0xffd23f, size: 9, sizeAttenuation: false, depthTest: false });
      const pts = new THREE.Points(dots, pm); pts.renderOrder = 45; grp.add(pts);
      if (P.length > 1) {
        const arr = []; for (let k = 0; k < loc.length - 1; k++) arr.push(loc[k].x, loc[k].y, loc[k].z, loc[k + 1].x, loc[k + 1].y, loc[k + 1].z);
        const g = new LineSegmentsGeometry(); g.setPositions(arr);
        if (!this.measureMat) { this.measureMat = new LineMaterial({ color: 0xffd23f, linewidth: 2.4, depthTest: false, transparent: true }); this.measureMat.resolution.set(innerWidth, innerHeight); this.lineMats.push(this.measureMat); }
        const l = new LineSegments2(g, this.measureMat); l.renderOrder = 44; grp.add(l);
        for (let k = 0; k < P.length - 1; k++) {
          const a = P[k], b = P[k + 1], h = Math.hypot(b.easting - a.easting, b.northing - a.northing);
          labels.push({ text: h >= 1000 ? `${(h / 1000).toFixed(2)} km` : `${h.toFixed(h < 10 ? 1 : 0)} m`, x: (loc[k].x + loc[k + 1].x) / 2, y: (loc[k].y + loc[k + 1].y) / 2, z: (loc[k].z + loc[k + 1].z) / 2 });
        }
      }
      this.world.add(grp); this.measureObj = grp;
    }
    this.labels.setMeasure(labels);
    this.emit('measure', P.slice());
    this.dirty = true;
  }

  // surveyed passage length (m) whose shots' midpoints satisfy test(E, N), in total and by cave
  lengthUnder(test) {
    if (!this.M) return { total: 0, byCave: [] };
    const [xc, yc] = this.M.origin, S = this.stPos, segs = this.segs, by = new Map();
    let total = 0;
    for (let k = 0; k < this.segSurvey.length; k++) {
      const a = segs[2 * k], b = segs[2 * k + 1];
      const E = (S[3 * a] + S[3 * b]) / 2 + xc, N = yc - (S[3 * a + 2] + S[3 * b + 2]) / 2;
      if (!test(E, N)) continue;
      const L = Math.hypot(S[3 * a] - S[3 * b], S[3 * a + 1] - S[3 * b + 1], S[3 * a + 2] - S[3 * b + 2]);
      const cave = this.M.caves.find(c => c.id === this.M.surveys[this.segSurvey[k]]?.cave_id)?.name || 'Cave';
      by.set(cave, (by.get(cave) || 0) + L); total += L;
    }
    return { total, byCave: [...by.entries()].sort((x, y) => y[1] - x[1]) };
  }

  flyToRings(rings) {
    const b = new THREE.Box3();
    for (const r of rings) for (const v of r) b.expandByPoint(this.toLocal(v[0], v[1], v[2]));
    if (b.isEmpty()) return;
    const c = b.getCenter(new THREE.Vector3()), s = b.getSize(new THREE.Vector3());
    this.flyToLocal(c, Math.max(80, Math.hypot(s.x, s.z) * 0.6), false);
  }

  // ---------------------------------------------------------------- property lines
  setParcels(list) {
    if (this.parcelObj) { this.world.remove(this.parcelObj); this.parcelObj.geometry.dispose(); this.parcelObj = null; }
    const L = list || [];
    if (!L.length || !this.origin) { this.labels.setParcels([]); return; }
    const [xc, yc, zc] = this.origin, pos = [], col = [], c = new THREE.Color(), labels = [];
    const TAGC = { club: '#7ee0a8', member: '#7fb4ff', friendly: '#d6e7a1', ask: '#ffd23f', closed: '#ff7a7a' };
    for (const p of L) {
      c.set(TAGC[p.tag] || '#e9e4d8');
      let best = null, ba = -1;
      for (const r of p.rings) {
        for (let i = 0; i < r.length - 1; i++) {
          const a = r[i], b = r[i + 1];
          pos.push(a[0] - xc, a[2] - zc + 0.6, -(a[1] - yc), b[0] - xc, b[2] - zc + 0.6, -(b[1] - yc));
          col.push(c.r, c.g, c.b, c.r, c.g, c.b);
        }
        let ar = 0; for (let i = 0; i < r.length - 1; i++) ar += r[i][0] * r[i + 1][1] - r[i + 1][0] * r[i][1];
        if (Math.abs(ar) > ba) { ba = Math.abs(ar); best = r; }
      }
      if (best) {
        let x = 0, y = 0; for (const v of best) { x += v[0]; y += v[1]; } x /= best.length; y /= best.length;
        const text = p.owner || p.pid;
        labels.push({ text: p.tag ? `${text} · ${{ club: 'BCCS', member: 'member' }[p.tag] || ''}`.replace(/ · $/, '') : text, x: x - xc, z: -(y - yc), tag: p.tag });
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    this.parcelObj = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.85, depthTest: false }));
    this.parcelObj.renderOrder = 6; this.parcelObj.visible = !!this.opts.parcels; this.world.add(this.parcelObj); this.clipObject(this.parcelObj, true);
    this.labels.setParcels(labels);
    this.dirty = true;
  }

  // ---------------------------------------------------------------- karst hydrology: basins, flow routes, springs
  // The basins and flow routes are painted into one image draped on the terrain (so they follow the ground at any
  // zoom); the springs are clickable rings.
  setHydro(doc) {
    this.hydroDoc = doc;
    if (this.springObj) { this.world.remove(this.springObj); this.springObj.geometry.dispose(); this.springObj = null; }
    const T = this.terrain?.shared;
    if (!doc || !this.origin || !T) { this.labels.setHydro([]); return; }
    const [xc, yc, zc] = this.origin, B = doc.basins || [], F = doc.flowpaths || [], S = doc.springs || [];
    // extent of everything, in local x / z
    let x0 = 1e12, x1 = -1e12, z0 = 1e12, z1 = -1e12;
    const ext = (v) => { const x = v[0] - xc, z = -(v[1] - yc); x0 = Math.min(x0, x); x1 = Math.max(x1, x); z0 = Math.min(z0, z); z1 = Math.max(z1, z); };
    for (const b of B) for (const r of b.rings) r.forEach(ext);
    for (const f of F) f.pts.forEach(ext);
    const D = this.opts.hydroDivides ? (doc.book_divides?.lines || []) : [];      // the Cove book's divides (Fig. 17.1)
    for (const l of D) l.forEach(ext);
    if (x1 > x0) {
      const pad = 60; x0 -= pad; x1 += pad; z0 -= pad; z1 += pad;
      const MAX = 4096, k = MAX / Math.max(x1 - x0, z1 - z0);
      const cv = document.createElement('canvas'); cv.width = Math.ceil((x1 - x0) * k); cv.height = Math.ceil((z1 - z0) * k);
      const g = cv.getContext('2d'), P = (v) => [(v[0] - xc - x0) * k, (-(v[1] - yc) - z0) * k];
      const path = (b) => { g.beginPath(); for (const r of b.rings) r.forEach((v, i) => { const [x, y] = P(v); i ? g.lineTo(x, y) : g.moveTo(x, y); }); };
      g.globalAlpha = 0.25;                                        // the shader reads alpha 0.25 as 'inside a basin'
      for (const b of B) { path(b); g.fillStyle = b.color; g.fill('evenodd'); }
      g.globalAlpha = 1; g.lineJoin = 'round'; g.lineCap = 'round';
      const px = Math.max(2, 15 * k);                              // ~15 m wide boundaries
      for (const b of B) { path(b); g.strokeStyle = '#141413'; g.lineWidth = px * 1.6; g.stroke(); }
      for (const b of B) { path(b); g.strokeStyle = b.color; g.lineWidth = px; g.stroke(); }
      for (const f of F) {                                         // flow routes: dashed, with an arrow at the spring
        const pts = f.pts.map(P); if (pts.length < 2) continue;
        g.beginPath(); pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
        g.setLineDash([px * 4, px * 2.5]); g.strokeStyle = '#141413'; g.lineWidth = px * 1.25; g.stroke();
        g.strokeStyle = '#ffffff'; g.lineWidth = px * 0.7; g.stroke(); g.setLineDash([]);
        const [ax, ay] = pts[pts.length - 1], [bx, by] = pts[pts.length - 2], an = Math.atan2(ay - by, ax - bx), L = px * 4;
        g.beginPath(); g.moveTo(ax, ay); g.lineTo(ax - L * Math.cos(an - 0.45), ay - L * Math.sin(an - 0.45));
        g.lineTo(ax - L * Math.cos(an + 0.45), ay - L * Math.sin(an + 0.45)); g.closePath();
        g.fillStyle = '#ffffff'; g.fill(); g.strokeStyle = '#141413'; g.lineWidth = px * 0.4; g.stroke();
      }
      g.setLineDash([px * 2, px * 1.2]);
      for (const l of D) {                                         // book divides: thin dashed red, over the basins
        if (l.length < 2) continue;
        g.beginPath(); l.map(P).forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
        g.strokeStyle = '#ffffff'; g.lineWidth = px * 0.9; g.stroke(); g.strokeStyle = '#c0392b'; g.lineWidth = px * 0.55; g.stroke();
      }
      g.setLineDash([]);
      const tex = new THREE.CanvasTexture(cv);
      tex.anisotropy = Math.min(8, this.renderer.capabilities.getMaxAnisotropy());     // raw colours, like the basemap tiles
      const old = T.hydroTex.value; T.hydroTex.value = tex; if (old !== this.terrain.blankDep) old.dispose();
      T.hydroBounds.value.set(x0, x1, z0, z1);
    }
    // springs
    if (S.length) {
      const pos = new Float32Array(3 * S.length), col = new Float32Array(3 * S.length), c = new THREE.Color();
      S.forEach((s, i) => { const z = s.z ?? this.groundAt(s.e, s.n); pos.set([s.e - xc, z - zc + 1, -(s.n - yc)], 3 * i); c.set(s.color || '#9085e9'); col.set([c.r, c.g, c.b], 3 * i); });
      const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.BufferAttribute(pos, 3)); g.setAttribute('color', new THREE.BufferAttribute(col, 3));
      if (!this.springTex) {
        const cv = document.createElement('canvas'); cv.width = cv.height = 48; const x = cv.getContext('2d');
        x.beginPath(); x.arc(24, 24, 15, 0, Math.PI * 2); x.fillStyle = '#ffffff'; x.fill(); x.lineWidth = 6; x.strokeStyle = '#141413'; x.stroke();
        x.beginPath(); x.arc(24, 24, 7, 0, Math.PI * 2); x.fillStyle = '#141413'; x.fill();
        this.springTex = new THREE.CanvasTexture(cv);
      }
      const m = new THREE.PointsMaterial({ size: 20, sizeAttenuation: false, vertexColors: true, map: this.springTex, transparent: true, alphaTest: 0.3, depthTest: false });
      this.springObj = new THREE.Points(g, m); this.springObj.renderOrder = 39; this.world.add(this.springObj); this.clipObject(this.springObj, true);
    }
    // labels: each spring, and each basin at the point deepest inside it
    const labels = S.map(s => ({ text: s.name, kind: 'spring', x: s.e - xc, z: -(s.n - yc) }));
    for (const b of B) { const p = interiorPoint(b.rings); if (p) labels.push({ text: b.name, kind: 'basin', x: p[0] - xc, z: -(p[1] - yc) }); }
    this.labels.setHydro(labels);
    this.applyOptions();
  }

  nearestSpring(x, y, rad = 14) {
    if (!this.springObj || !this.springObj.visible) return null;
    this.world.updateMatrixWorld();
    const a = this.springObj.geometry.attributes.position, v = new THREE.Vector3(), S = this.hydroDoc.springs;
    let best = null, bd = rad * rad;
    for (let i = 0; i < S.length; i++) {
      v.fromBufferAttribute(a, i); const s = this.screen(v, this.world); if (!s) continue;
      const d = (s[0] - x) ** 2 + (s[1] - y) ** 2; if (d < bd) { bd = d; best = S[i]; }
    }
    return best;
  }

  // ---------------------------------------------------------------- sinkhole finder results
  setSinks(list, colorOf = null) {
    if (this.sinkObj) { this.world.remove(this.sinkObj); this.sinkObj.geometry.dispose(); this.sinkObj = null; }
    this.sinkList = list || [];
    if (!this.sinkList.length || !this.origin) { this.dirty = true; return; }
    const [xc, yc, zc] = this.origin, n = this.sinkList.length;
    const pos = new Float32Array(3 * n), col = new Float32Array(3 * n), c = new THREE.Color();
    const lo = new THREE.Color('#ffe08a'), hi = new THREE.Color('#e0452b');
    this.sinkList.forEach((s, i) => {
      pos.set([s.e - xc, s.floor - zc + 0.3, -(s.n - yc)], 3 * i);
      const cc = colorOf && colorOf(s);
      if (cc) c.set(cc); else c.copy(lo).lerp(hi, Math.min(1, Math.log(1 + s.depth) / Math.log(7)));
      col.set([c.r, c.g, c.b], 3 * i);
    });
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.BufferAttribute(pos, 3)); g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    if (!this.ringTex) {
      const cv = document.createElement('canvas'); cv.width = cv.height = 32; const x = cv.getContext('2d');
      x.beginPath(); x.arc(16, 16, 11, 0, Math.PI * 2); x.lineWidth = 5; x.strokeStyle = '#141413'; x.stroke(); x.lineWidth = 3; x.strokeStyle = '#ffffff'; x.stroke();
      this.ringTex = new THREE.CanvasTexture(cv);
    }
    const m = new THREE.PointsMaterial({ size: 13, sizeAttenuation: false, vertexColors: true, map: this.ringTex, transparent: true, alphaTest: 0.3, depthTest: false });
    this.sinkObj = new THREE.Points(g, m); this.sinkObj.renderOrder = 38; this.world.add(this.sinkObj); this.clipObject(this.sinkObj, true);
    this.dirty = true;
  }

  nearestSink(x, y, rad = 10) {
    if (!this.sinkObj || !this.sinkList.length) return null;
    this.world.updateMatrixWorld();
    const a = this.sinkObj.geometry.attributes.position, v = new THREE.Vector3();
    let best = null, bd = rad * rad;
    for (let i = 0; i < this.sinkList.length; i++) {
      v.fromBufferAttribute(a, i); const s = this.screen(v, this.world); if (!s) continue;
      const d = (s[0] - x) ** 2 + (s[1] - y) ** 2; if (d < bd) { bd = d; best = this.sinkList[i]; }
    }
    return best;
  }

  cursorAt(x, y) {                       // live ground readout under the mouse
    const g = this.groundRay(x, y);
    this.emit('cursor', g ? this.fromLocal(g) : null);
  }

  nearestDepression(x, y) {
    if (!this.M || !this.opts.depressions) return null;
    let best = null, bd = 18 * 18; this.world.updateMatrixWorld();
    (this.M.depressions || []).forEach((d, i) => {
      const s = this.screen(this.toLocal(d.easting, d.northing, d.ground), this.world); if (!s) return;
      const dd = (s[0] - x) ** 2 + (s[1] - y) ** 2; if (dd < bd) { bd = dd; best = { i, d }; }
    });
    return best;
  }

  startPick(kind, cb) { this.pickMode = { kind, cb }; this.canvas.style.cursor = 'crosshair'; }
  cancelPick() { this.pickMode = null; this.canvas.style.cursor = ''; }
  finishPick(v) { const cb = this.pickMode.cb; this.cancelPick(); cb(v); }

  hover() {
    const tip = this.tip;
    if (!this.mouse) return;
    const [x, y] = this.mouse;
    let html = null;
    const m = this.nearestMarker(x, y);
    if (m) {
      const p = m.place, st = placeStyle(p);
      const sub = p.type === 'feature' ? `${st.label}${p.status ? ' · ' + p.status : ''}`
        : p.type === 'observation' ? `${st.label}${p.date ? ' · ' + p.date : ''}${p.count != null ? ' · ' + p.count : ''}`
          : `${p.study} · ${p.kind}${p.result ? ' · ' + p.result : ''}`;
      html = `<b>${esc(p.name)}</b><div class="muted">${esc(sub)}</div>`;
      this.hoverMarker && (this.hoverMarker.visible = false);
    } else if (!this.pickMode && !this.measuring && this.nearestSpring(x, y)) {
      const sp = this.nearestSpring(x, y), b = (this.hydroDoc.basins || []).find(q => q.key === sp.basin);
      html = `<b>${esc(sp.name)}</b><div class="muted">Spring${b ? ' · ' + esc(b.name) : ''}</div>`;
      this.hoverMarker && (this.hoverMarker.visible = false);
    } else if (!this.pickMode && !this.measuring && !this.lineDraw && this.nearestWater(x, y, 8, false)) {
      html = this.waterTip(this.nearestWater(x, y, 8, false));
      this.hoverMarker && (this.hoverMarker.visible = false);
    } else if (this.pickMode?.kind === 'shot') {
      const h = this.nearestShot(x, y, 16), hm = this.hoverMarker;
      if (h) {
        const S = this.stPos, i = h.ia, j = h.ib, t = h.t;
        html = `<b>${esc(h.a)} → ${esc(h.b)}</b><div>${(h.along / 0.3048).toFixed(1)} ft (${h.along.toFixed(1)} m) from ${esc(h.a)}</div>`;
        hm.position.set(S[3 * i] + (S[3 * j] - S[3 * i]) * t, S[3 * i + 1] + (S[3 * j + 1] - S[3 * i + 1]) * t, S[3 * i + 2] + (S[3 * j + 2] - S[3 * i + 2]) * t);
        const d = this.camera.position.distanceTo(hm.getWorldPosition(new THREE.Vector3())) * 0.006; hm.scale.set(d, d / this.opts.ve, d); hm.visible = true;
      } else hm && (hm.visible = false);
    } else if (!this.pickMode || this.pickMode.kind === 'station') {
      const i = this.nearestStation(x, y, this.pickMode ? 20 : 14);
      if (i >= 0) {
        const s = this.stationInfo(i);
        html = `<b>${esc(s.name)}</b><div class="muted">${esc(s.survey.name || '')}${s.survey.date ? ' · ' + s.survey.date : ''}</div>
          <div>Elev ${fmtM(s.elevation)}</div><div>Depth ${s.depth < 0 ? '<span style="color:#e87ba4">' : ''}${fmtM(s.depth)}${s.depth < 0 ? '</span>' : ''}</div>`
          + (s.formation ? `<div><span class="sw" style="display:inline-block;width:9px;height:9px;border-radius:2px;margin-right:5px;background:${s.formation.unit.color}"></span>${esc(s.formation.unit.name)} <span class="muted">(model)</span></div>` : '');
        const hm = this.hoverMarker; hm.position.set(this.stPos[3 * i], this.stPos[3 * i + 1], this.stPos[3 * i + 2]);
        const d = this.camera.position.distanceTo(hm.getWorldPosition(new THREE.Vector3())) * 0.006; hm.scale.set(d, d / this.opts.ve, d); hm.visible = true;
      } else {
        this.hoverMarker && (this.hoverMarker.visible = false);
        const w = !this.pickMode && !this.measuring && !this.lineDraw && this.nearestWater(x, y, 8, true);
        if (w) html = this.waterTip(w);
      }
    }
    if (this.pickMode && !html) html = this.pickMode.kind === 'station' ? 'Click a survey station' : this.pickMode.kind === 'shot' ? 'Click on the survey line' : 'Click the ground';
    if (this.measuring && !html) html = 'Click to add a point (snaps to survey stations)';
    const key = html + '|' + (this.hoverMarker?.visible ? this.hoverMarker.position.x : '');
    if (key !== this.lastHover) { this.lastHover = key; this.dirty = true; }
    if (!html) { tip.style.display = 'none'; return; }
    if (tip.innerHTML !== html) tip.innerHTML = html;
    tip.style.display = 'block';
    const w = tip.offsetWidth, h = tip.offsetHeight;
    tip.style.left = (x + 16 + w > innerWidth ? x - w - 16 : x + 16) + 'px'; tip.style.top = (y + 16 + h > innerHeight ? y - h - 16 : y + 16) + 'px';
  }

  // ---------------------------------------------------------------- camera
  bounds() {
    const b = new THREE.Box3();
    if (this.M && this.names.length) b.setFromBufferAttribute(new THREE.BufferAttribute(this.stPos, 3));
    else if (this.terrain) return this.areaBounds();
    return b;
  }

  areaBounds() {
    const a = this.terrain.area, r = this.terrain.tile(0, 0, 0);
    return new THREE.Box3(new THREE.Vector3(a.x0, r.hmin ?? 0, a.z0), new THREE.Vector3(a.x1, r.hmax ?? 300, a.z1));
  }

  view(name, instant) {
    const area = name === 'area' && this.terrain;
    const b = area ? this.areaBounds() : this.bounds(), c = b.getCenter(new THREE.Vector3()), s = b.getSize(new THREE.Vector3());
    if (area) name = 'oblique';
    c.y *= this.opts.ve;
    const dir = { oblique: [0.35, 0.95, 0.75], plan: [0, 1, 0.0005], profile: [0.7, 0.03, 0.7] }[name] || [0.35, 0.95, 0.75];
    const r = 0.5 * Math.hypot(s.x, s.y * this.opts.ve, s.z) || 1000;
    const vf = THREE.MathUtils.degToRad(this.camera.fov), hf = 2 * Math.atan(Math.tan(vf / 2) * this.camera.aspect * 0.8);
    const d = r / Math.sin(Math.min(vf, hf) / 2) * (name === 'plan' ? 0.9 : 0.74);
    this.flyTo(c, c.clone().add(new THREE.Vector3(...dir).normalize().multiplyScalar(d)), instant);
  }

  flyTo(target, position, instant) {
    if (instant) { this.controls.target.copy(target); this.camera.position.copy(position); this.dirty = true; return; }
    const p0 = this.camera.position.clone(), t0 = this.controls.target.clone(), st = performance.now();
    this.anim = (now) => {
      const t = Math.min(1, (now - st) / 700), e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      this.camera.position.lerpVectors(p0, position, e); this.controls.target.lerpVectors(t0, target, e);
      if (t >= 1) this.anim = null;
    };
  }

  flyToLocal(v, radius = 120, underground = false) {
    const t = v.clone(); if (underground) t.y += this.opts.shift; t.y *= this.opts.ve;
    const off = this.camera.position.clone().sub(this.controls.target).normalize();
    if (off.y < 0.35) { off.y = 0.6; off.normalize(); }
    this.flyTo(t, t.clone().add(off.multiplyScalar(radius * 2.2)));
  }

  flyToLocs(locs) {
    const b = new THREE.Box3();
    for (const l of locs) { const pl = this.placeLocal({ loc: l }); if (!pl) continue; const v = pl.v.clone(); if (pl.underground) v.y += this.opts.shift; b.expandByPoint(v); }
    if (b.isEmpty()) return;
    const c = b.getCenter(new THREE.Vector3()), s = b.getSize(new THREE.Vector3());
    this.flyToLocal(c, Math.max(90, Math.hypot(s.x, s.z) * 0.55), false);
  }

  flyToPlace(p) { const pl = this.placeLocal(p); if (pl) this.flyToLocal(pl.v, 110, pl.underground); }
  flyToStation(name) { const i = this.nameIdx?.get(name); if (i == null) return false;
    this.flyToLocal(new THREE.Vector3(this.stPos[3 * i], this.stPos[3 * i + 1], this.stPos[3 * i + 2]), 80, true); return this.stationInfo(i); }
  flyToEN(E, N, Z, radius = 120) { this.flyToLocal(this.toLocal(E, N, Z), radius, false); }

  flyToSurveys(set) {
    const b = new THREE.Box3(), v = new THREE.Vector3();
    for (let i = 0; i < this.names.length; i++) if (set.has(this.stSurvey[i])) b.expandByPoint(v.set(this.stPos[3 * i], this.stPos[3 * i + 1], this.stPos[3 * i + 2]));
    if (b.isEmpty()) return;
    const c = b.getCenter(new THREE.Vector3()), s = b.getSize(new THREE.Vector3());
    this.flyToLocal(c, Math.max(60, Math.hypot(s.x, s.z) * 0.6), true);
  }

  frame(now) {
    if (this.anim) { this.anim(now); this.dirty = true; }
    this.controls.update();
    if (this.hoverDue && this.mouse) {
      this.hoverDue = false; this.hover();
      if (now - (this.lastCursor || 0) > 70) { this.lastCursor = now; this.cursorAt(...this.mouse); } else this.cursorDue = true;
    } else if (this.cursorDue && this.mouse && now - this.lastCursor > 70) { this.cursorDue = false; this.lastCursor = now; this.cursorAt(...this.mouse); }
    if (!this.dirty) return;
    this.dirty = false;
    const dist = this.camera.position.distanceTo(this.controls.target);
    const near = Math.min(50, Math.max(0.5, dist * 0.002)), far = Math.max(40000, dist * 6);
    if (Math.abs(near - this.camera.near) > this.camera.near * 0.2 || far !== this.camera.far) {
      this.camera.near = near; this.camera.far = far; this.camera.updateProjectionMatrix();
    }
    if (this.terrain) {
      this.terrain.update(this.camera, window.innerHeight);
      const busy = this.terrain.busy > 0;
      if (busy !== this.lastBusy) { this.lastBusy = busy; this.emit('busy', busy); }
    }
    const az = Math.round(THREE.MathUtils.radToDeg(this.controls.getAzimuthalAngle()));
    if (az !== this.lastAz) { this.lastAz = az; this.emit('camera', az); }
    this.renderer.render(this.scene, this.camera);
    this.labels.update();
  }
}

function textSprite(text) {
  const c = document.createElement('canvas'), g = c.getContext('2d'), f = 'bold 30px system-ui, sans-serif';
  g.font = f; const w = Math.ceil(g.measureText(text).width) + 20; c.width = w; c.height = 44;
  g.font = f; g.fillStyle = 'rgba(20,20,19,.75)'; g.beginPath(); g.roundRect(0, 0, w, 44, 8); g.fill();
  g.fillStyle = '#faf9f5'; g.textBaseline = 'middle'; g.fillText(text, 10, 23);
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace;
  const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: t, depthTest: false, sizeAttenuation: false }));
  sp.renderOrder = 41; sp.userData = { w, h: 44 };
  return sp;
}

function esc(s) { return String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c])); }

// a label point well inside a polygon (the grid point farthest from its edges)
function interiorPoint(rings) {
  const R = rings.filter(r => r.length > 3); if (!R.length) return null;
  let x0 = 1e12, x1 = -1e12, y0 = 1e12, y1 = -1e12;
  for (const r of R) for (const [x, y] of r) { x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y); }
  const inside = (X, Y) => { let k = false; for (const r of R) for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const [xi, yi] = r[i], [xj, yj] = r[j]; if ((yi > Y) !== (yj > Y) && X < (xj - xi) * (Y - yi) / (yj - yi) + xi) k = !k; } return k; };
  const edge = (X, Y) => { let d = 1e18; for (const r of R) for (let i = 0; i < r.length - 1; i++) {
    const [ax, ay] = r[i], [bx, by] = r[i + 1], dx = bx - ax, dy = by - ay, L = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((X - ax) * dx + (Y - ay) * dy) / L)); d = Math.min(d, (X - ax - t * dx) ** 2 + (Y - ay - t * dy) ** 2); } return d; };
  let best = null, bd = -1; const n = 40;
  for (let i = 0; i <= n; i++) for (let j = 0; j <= n; j++) {
    const X = x0 + (x1 - x0) * i / n, Y = y0 + (y1 - y0) * j / n;
    if (!inside(X, Y)) continue; const d = edge(X, Y); if (d > bd) { bd = d; best = [X, Y]; }
  }
  return best;
}

Object.assign(Viewer.prototype, ProfileMixin, WaterMixin, CoveMixin);
